import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight, ArrowLeft } from "lucide-react";
import axios from "axios";
import { PageSEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["All", "AI & Technology", "Industry Insights", "Streaming & Distribution", "Indie Filmmaking", "Creative Process", "Culture & Trends"];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function getCategoryColor(cat) {
  const colors = {
    "AI & Technology": "text-[#38BDF8] border-[#38BDF8]/30",
    "Industry Insights": "text-[#F472B6] border-[#F472B6]/30",
    "Streaming & Distribution": "text-[#A78BFA] border-[#A78BFA]/30",
    "Indie Filmmaking": "text-[#34D399] border-[#34D399]/30",
    "Creative Process": "text-[#FBBF24] border-[#FBBF24]/30",
    "Culture & Trends": "text-[#FB923C] border-[#FB923C]/30",
  };
  return colors[cat] || "text-[#71717A] border-[#71717A]/30";
}

export default function ThoughtsPage() {
  const [data, setData] = useState({ thoughts: [], total: 0, page: 1, pages: 0 });
  const [activeCategory, setActiveCategory] = useState("All");
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchThoughts = async () => {
      setLoaded(false);
      try {
        const params = new URLSearchParams({ page, limit: 12 });
        if (activeCategory !== "All") params.set("category", activeCategory);
        const res = await axios.get(`${API}/thoughts?${params}`);
        setData(res.data);
      } catch (e) {
        console.error("Failed to fetch thoughts", e);
      } finally {
        setLoaded(true);
      }
    };
    fetchThoughts();
  }, [activeCategory, page]);

  useEffect(() => {
    setPage(1);
  }, [activeCategory]);

  return (
    <div data-testid="thoughts-page" className="min-h-screen pt-36 pb-28 px-6 md:px-12 lg:px-24">
      <PageSEO
        title="Thoughts"
        description="Insights, opinions, and perspectives from Mist Media on AI in filmmaking, streaming, indie production, and the future of storytelling."
        path="/thoughts"
      />

      <div className="max-w-[1400px] mx-auto">
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
            <li><Link to="/" className="text-[#3f3f46] hover:text-white transition-colors">Home</Link></li>
            <li><ChevronRight size={10} className="text-[#27272A]" /></li>
            <li className="text-[#71717A]">Thoughts</li>
          </ol>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-px bg-[#38BDF8]" aria-hidden="true" />
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">Our Perspective</p>
          </div>
          <h1 className="font-['Syne'] text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-[-0.04em] text-white">
            Thoughts
          </h1>
          <p className="mt-4 text-sm text-[#52525B] max-w-lg">
            Where we share our take on the forces shaping film, TV, and digital storytelling.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-14"
        >
          <p className="text-[9px] uppercase tracking-[0.3em] font-bold text-[#3f3f46] mb-3">Category</p>
          <div className="flex flex-wrap gap-1.5" data-testid="category-filter" role="group" aria-label="Filter by category">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                aria-pressed={activeCategory === cat}
                className={`btn-press text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2 rounded-none border transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-white text-[#050505] border-white"
                    : "bg-transparent text-[#52525B] border-[#1A1A1A] hover:border-[#3f3f46] hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </motion.div>

        {loaded && (
          <div className="mb-8">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#3f3f46]">
              {data.total} Post{data.total !== 1 ? "s" : ""}
            </p>
          </div>
        )}

        {/* Posts Grid */}
        {!loaded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-card h-64 skeleton rounded-none" />
            ))}
          </div>
        )}

        {loaded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.thoughts.map((post, i) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Link
                  to={`/thoughts/${post.slug}`}
                  data-testid={`thought-card-${post.slug}`}
                  className="group block p-6 border border-[#1A1A1A] hover:border-[#27272A] transition-all duration-500 h-full"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 border rounded-none ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                    <span className="text-[10px] text-[#3f3f46]">{formatDate(post.published_date)}</span>
                  </div>
                  <h2 className="font-['Syne'] text-lg font-bold text-white group-hover:text-[#38BDF8] transition-colors duration-300 mb-3 leading-tight">
                    {post.title}
                  </h2>
                  <p className="text-xs text-[#71717A] leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-[10px] text-[#3f3f46] group-hover:text-[#38BDF8] transition-colors uppercase tracking-[0.15em] font-bold">
                    Read More
                    <ArrowRight size={10} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        {loaded && data.thoughts.length === 0 && (
          <div className="text-center py-24">
            <p className="text-[#52525B] text-sm">No posts match the selected filter.</p>
          </div>
        )}

        {/* Pagination */}
        {loaded && data.pages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-16">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="thoughts-prev-page"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2 border border-[#1A1A1A] text-[#A1A1AA] hover:border-[#52525B] hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none"
            >
              <ArrowLeft size={10} /> Previous
            </button>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#3f3f46]">
              {page} / {data.pages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(data.pages, p + 1))}
              disabled={page >= data.pages}
              data-testid="thoughts-next-page"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2 border border-[#1A1A1A] text-[#A1A1AA] hover:border-[#52525B] hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none"
            >
              Next <ArrowRight size={10} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
